import type { CategoryPaginator, CategoryQueryOptions } from '@/types';
import { useInfiniteQuery } from 'react-query';
import client from './client';
import { API_ENDPOINTS } from './client/api-endpoints';
import { mapPaginatorData } from '@/framework/utils/data-mappers';
import { useMemo } from 'react';

export function useCategories(options?: Partial<CategoryQueryOptions>, enabled = true) {
  const formattedOptions = {
    ...options,
    page: 1,
    limit: 100,
  };

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery<CategoryPaginator, Error>(
    [API_ENDPOINTS.CATEGORIES, formattedOptions],
    ({ queryKey, pageParam }) =>
      client.categories.all(Object.assign({}, queryKey[1], pageParam)),
    {
      enabled: Boolean(enabled),
      staleTime: 12 * 60 * 60 * 1000, // 12 hours
      getNextPageParam: ({ current_page, last_page }) =>
        last_page > current_page && { page: current_page + 1 },
    },
  );

  function handleLoadMore() {
    fetchNextPage();
  }
  const categories = useMemo(
    () => data?.pages?.flatMap((page) => page.data) ?? [],
    [data],
  );

  const childrenIds = useMemo(
    () =>
      new Set(
        categories.flatMap(
          (category) => category?.children?.map((child) => child.id) || [],
        ),
      ),
    [categories],
  );

  const parentCategories = useMemo(
    () => categories.filter((category) => !childrenIds.has(category.id)),
    [categories, childrenIds],
  );

  return {
    categories: parentCategories,
    paginatorInfo: Array.isArray(data?.pages)
      ? mapPaginatorData(data?.pages[data.pages.length - 1])
      : null,
    isLoading,
    error,
    isFetching,
    isLoadingMore: isFetchingNextPage,
    loadMore: handleLoadMore,
    hasMore: Boolean(hasNextPage),
  };
}
